/** API line fields for header trade adjustments already distributed to the row. */
export interface DistributedLineAdjustmentSource {
  distributedDiscountExtendedAmount?: number;
  DistributedDiscountExtendedAmount?: number;
  distributedChargeExtendedAmount?: number;
  DistributedChargeExtendedAmount?: number;
  lineOnlyDiscountMoney?: number;
  LineOnlyDiscountMoney?: number;
  lineOnlyChargeMoney?: number;
  LineOnlyChargeMoney?: number;
  lineDiscountBeforeDistributionAmount?: number;
  LineDiscountBeforeDistributionAmount?: number;
  lineChargeBeforeDistributionAmount?: number;
  LineChargeBeforeDistributionAmount?: number;
  discountEnteredAtLineLevel?: boolean;
  DiscountEnteredAtLineLevel?: boolean;
  chargeEnteredAtLineLevel?: boolean;
  ChargeEnteredAtLineLevel?: boolean;
  discountAmount?: number;
  DiscountAmount?: number;
  chargeAmount?: number;
  ChargeAmount?: number;
  quantity?: number;
  Quantity?: number;
}

function readNonNegativeNumber(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function hasDistributedDiscountExtendedAmount(
  line: DistributedLineAdjustmentSource | null | undefined,
): boolean {
  return (
    line?.distributedDiscountExtendedAmount !== undefined &&
    line?.distributedDiscountExtendedAmount !== null
  ) || (
    line?.DistributedDiscountExtendedAmount !== undefined &&
    line?.DistributedDiscountExtendedAmount !== null
  );
}

export function hasDistributedChargeExtendedAmount(
  line: DistributedLineAdjustmentSource | null | undefined,
): boolean {
  return (
    line?.distributedChargeExtendedAmount !== undefined &&
    line?.distributedChargeExtendedAmount !== null
  ) || (
    line?.DistributedChargeExtendedAmount !== undefined &&
    line?.DistributedChargeExtendedAmount !== null
  );
}

export function readDistributedDiscountExtendedAmount(
  line: DistributedLineAdjustmentSource | null | undefined,
): number {
  const raw =
    line?.distributedDiscountExtendedAmount ??
    line?.DistributedDiscountExtendedAmount ??
    0;
  return readNonNegativeNumber(raw);
}

export function readDistributedChargeExtendedAmount(
  line: DistributedLineAdjustmentSource | null | undefined,
): number {
  const raw =
    line?.distributedChargeExtendedAmount ??
    line?.DistributedChargeExtendedAmount ??
    0;
  return readNonNegativeNumber(raw);
}

export function readLineOnlyDiscountExtendedAmount(
  line: DistributedLineAdjustmentSource | null | undefined,
): number {
  const perUnit = readLineOnlyDiscountPerUnitMoney(line);
  if (perUnit > 0) {
    const originalQty = readNonNegativeNumber(
      line?.quantity ?? line?.Quantity,
    );
    return originalQty > 0 ? perUnit * originalQty : perUnit;
  }

  const raw =
    line?.lineDiscountBeforeDistributionAmount ??
    line?.LineDiscountBeforeDistributionAmount ??
    0;
  return readNonNegativeNumber(raw);
}

/** Per-unit line-only discount from API (`lineOnlyDiscountMoney` / `discountMoney`). */
export function readLineOnlyDiscountPerUnitMoney(
  line: DistributedLineAdjustmentSource | null | undefined,
): number {
  const raw =
    line?.lineOnlyDiscountMoney ??
    line?.LineOnlyDiscountMoney ??
    (line as { discountMoney?: number })?.discountMoney ??
    (line as { DiscountMoney?: number })?.DiscountMoney ??
    0;
  return readNonNegativeNumber(raw);
}

export function readLineOnlyChargeExtendedAmount(
  line: DistributedLineAdjustmentSource | null | undefined,
): number {
  const perUnit = readLineOnlyChargePerUnitMoney(line);
  if (perUnit > 0) {
    const originalQty = readNonNegativeNumber(
      line?.quantity ?? line?.Quantity,
    );
    return originalQty > 0 ? perUnit * originalQty : perUnit;
  }

  const raw =
    line?.lineChargeBeforeDistributionAmount ??
    line?.LineChargeBeforeDistributionAmount ??
    0;
  return readNonNegativeNumber(raw);
}

/** Per-unit line-only charge from API (`lineOnlyChargeMoney` / `chargeMoney`). */
export function readLineOnlyChargePerUnitMoney(
  line: DistributedLineAdjustmentSource | null | undefined,
): number {
  const raw =
    line?.lineOnlyChargeMoney ??
    line?.LineOnlyChargeMoney ??
    (line as { chargeMoney?: number })?.chargeMoney ??
    (line as { ChargeMoney?: number })?.ChargeMoney ??
    0;
  return readNonNegativeNumber(raw);
}

/** True when the user entered discount on this line (not header-only distribution). */
export function isLineLevelTradeDiscountEntered(
  line: DistributedLineAdjustmentSource | null | undefined,
): boolean {
  if (
    line?.discountEnteredAtLineLevel === true ||
    line?.DiscountEnteredAtLineLevel === true
  ) {
    return true;
  }
  if (readLineOnlyDiscountExtendedAmount(line) > 0) {
    return true;
  }
  const rawDiscount = Number(line?.discountAmount ?? line?.DiscountAmount ?? 0);
  if (
    Number.isFinite(rawDiscount) &&
    rawDiscount > 0 &&
    readDistributedDiscountExtendedAmount(line) <= 0
  ) {
    return true;
  }
  return false;
}

/** True when the user entered charge on this line (not header-only distribution). */
export function isLineLevelTradeChargeEntered(
  line: DistributedLineAdjustmentSource | null | undefined,
): boolean {
  if (
    line?.chargeEnteredAtLineLevel === true ||
    line?.ChargeEnteredAtLineLevel === true
  ) {
    return true;
  }
  if (readLineOnlyChargeExtendedAmount(line) > 0) {
    return true;
  }
  const rawCharge = Number(line?.chargeAmount ?? line?.ChargeAmount ?? 0);
  if (
    Number.isFinite(rawCharge) &&
    rawCharge > 0 &&
    readDistributedChargeExtendedAmount(line) <= 0
  ) {
    return true;
  }
  return false;
}

function prorateExtendedAmount(
  extendedAmount: number,
  originalLine: DistributedLineAdjustmentSource,
  returnQuantity: number,
): number {
  if (extendedAmount <= 0) {
    return 0;
  }
  const originalQty = readNonNegativeNumber(
    originalLine.quantity ?? originalLine.Quantity,
  );
  const returnQty = readNonNegativeNumber(returnQuantity);
  if (returnQty <= 0) {
    return 0;
  }
  if (originalQty > 0 && returnQty < originalQty) {
    return (returnQty / originalQty) * extendedAmount;
  }
  return extendedAmount;
}

export function resolveLineOnlyDiscountAmount(
  originalLine: DistributedLineAdjustmentSource,
  returnQuantity: number,
): number {
  const perUnit = readLineOnlyDiscountPerUnitMoney(originalLine);
  const returnQty = readNonNegativeNumber(returnQuantity);
  if (perUnit > 0 && returnQty > 0) {
    return perUnit * returnQty;
  }

  return prorateExtendedAmount(
    readLineOnlyDiscountExtendedAmount(originalLine),
    originalLine,
    returnQuantity,
  );
}

export function resolveLineOnlyChargeAmount(
  originalLine: DistributedLineAdjustmentSource,
  returnQuantity: number,
): number {
  const perUnit = readLineOnlyChargePerUnitMoney(originalLine);
  const returnQty = readNonNegativeNumber(returnQuantity);
  if (perUnit > 0 && returnQty > 0) {
    return perUnit * returnQty;
  }

  return prorateExtendedAmount(
    readLineOnlyChargeExtendedAmount(originalLine),
    originalLine,
    returnQuantity,
  );
}

/**
 * Prorated line discount share for return rows.
 * Uses backend `distributedDiscountExtendedAmount` when the original sale had header trade.
 */
export function resolveDistributedLineDiscountAmount(
  originalLine: DistributedLineAdjustmentSource,
  returnQuantity: number,
): number {
  return prorateExtendedAmount(
    readDistributedDiscountExtendedAmount(originalLine),
    originalLine,
    returnQuantity,
  );
}

/**
 * Prorated line charge share for return rows (header charge distributed on server).
 */
export function resolveDistributedLineChargeAmount(
  originalLine: DistributedLineAdjustmentSource,
  returnQuantity: number,
): number {
  return prorateExtendedAmount(
    readDistributedChargeExtendedAmount(originalLine),
    originalLine,
    returnQuantity,
  );
}

/** Line discount sent on return payload: distributed header share + optional line-only share. */
export function resolveReturnLinePayloadDiscountAmount(
  originalLine: DistributedLineAdjustmentSource,
  returnQuantity: number,
): number {
  const distributed = resolveDistributedLineDiscountAmount(
    originalLine,
    returnQuantity,
  );
  const lineOnly = isLineLevelTradeDiscountEntered(originalLine)
    ? resolveLineOnlyDiscountAmount(originalLine, returnQuantity)
    : 0;
  return distributed + lineOnly;
}

/** Line charge sent on return payload: distributed header share + optional line-only share. */
export function resolveReturnLinePayloadChargeAmount(
  originalLine: DistributedLineAdjustmentSource,
  returnQuantity: number,
): number {
  const distributed = resolveDistributedLineChargeAmount(
    originalLine,
    returnQuantity,
  );
  const lineOnly = isLineLevelTradeChargeEntered(originalLine)
    ? resolveLineOnlyChargeAmount(originalLine, returnQuantity)
    : 0;
  return distributed + lineOnly;
}

/**
 * Trade discount/charge mode: true = percent (%), false = fixed monetary amount.
 * Pure read-side coercion for form snapshots and API fields — never mutates form state.
 */
export function coerceTradeAdjustmentIsPercent(value: unknown): boolean {
  if (value === true || value === 1) {
    return true;
  }
  if (value === false || value === 0) {
    return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }
  return false;
}
